import PropTypes from 'prop-types';
import { useState } from 'react';
import { useIntl } from 'react-intl';

// material-ui
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third party
import { format, startOfDay } from 'date-fns';

// project imports
import MainCard from 'components/MainCard';
import { DebouncedInput } from 'components/third-party/react-table';

// ant design icons
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';

// ==============================|| ABC HISTORICAL TABLE ||============================== //

// Common values for dropdowns
const commonSettings = ['Classroom', 'Playground', 'Cafeteria', 'Hallway', 'Therapy Room', 'Outdoor Area', 'Home', 'Community'];

// eslint-disable-next-line no-unused-vars
const commonAntecedents = [
  'Denied access to preferred item',
  'Difficult task presented',
  'Transition between activities',
  'Asked to wait',
  'Peer interaction',
  'Change in routine',
  'Loud noise',
  'Being told "no"',
  'End of preferred activity',
  'Other'
];

// eslint-disable-next-line no-unused-vars
const commonBehaviors = [
  'Tantrum (crying/screaming)',
  'Aggression (hitting/kicking)',
  'Self-injury',
  'Property destruction',
  'Elopement',
  'Non-compliance',
  'Stereotypy',
  'Other'
];

// eslint-disable-next-line no-unused-vars
const commonConsequences = [
  'Removed from situation',
  'Given item/activity',
  'Redirected to another activity',
  'Ignored behavior',
  'Physical prompt',
  'Verbal reprimand',
  'Time in safe space',
  'Other'
];

export default function ABCHistoricalTable({ data, setData, startDate, endDate, setStartDate, setEndDate, dataEntryMode = false }) {
  const intl = useIntl();
  const [globalFilter, setGlobalFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesIndex, setNotesIndex] = useState(null);
  const [notesText, setNotesText] = useState('');

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRecord(null);
  };

  // eslint-disable-next-line no-unused-vars
  const handleOpenNotesDialog = (index, currentNotes) => {
    setNotesIndex(index);
    setNotesText(currentNotes || '');
    setNotesDialogOpen(true);
  };

  const handleCloseNotesDialog = () => {
    setNotesDialogOpen(false);
    setNotesIndex(null);
    setNotesText('');
  };

  const handleSaveNotes = () => {
    if (notesIndex !== null) {
      updateField(notesIndex, 'notes', notesText);
    }
    handleCloseNotesDialog();
  };

  // Update field value
  const updateField = (index, field, value) => {
    setData((old) => {
      const newData = JSON.parse(JSON.stringify(old)); // Deep clone
      if (newData[index]) {
        newData[index][field] = value;
      }
      return newData;
    });
  };

  // Delete row
  const deleteRow = (index) => {
    setData((old) => old.filter((_, i) => i !== index));
  };

  // Filter data by date range (only for historical mode)
  const filteredData =
    !dataEntryMode && (startDate || endDate)
      ? data.filter((row) => {
          const rowDate = new Date(row.date);
          const start = startDate ? new Date(startDate) : null;
          const end = endDate ? new Date(endDate) : null;

          if (start) start.setHours(0, 0, 0, 0);
          if (end) end.setHours(23, 59, 59, 999);
          rowDate.setHours(12, 0, 0, 0);

          if (start && end) {
            return rowDate >= start && rowDate <= end;
          } else if (start) {
            return rowDate >= start;
          } else if (end) {
            return rowDate <= end;
          }
          return true;
        })
      : data;

  // Paginate data
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MainCard content={false}>
        {/* Filters - Only show in Historical mode */}
        {!dataEntryMode && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ p: 2, alignItems: 'center' }}>
            <DebouncedInput
              value={globalFilter ?? ''}
              onFilterChange={(value) => setGlobalFilter(String(value))}
              placeholder="Search all records..."
              sx={{ flexGrow: 1 }}
            />
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue);
                setPage(0);
              }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue);
                setPage(0);
              }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
          </Stack>
        )}

        {/* Table */}
        <TableContainer>
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': {
                padding: '6px 8px',
                fontSize: '0.75rem'
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>{intl.formatMessage({ id: 'form.label.setting' })}</TableCell>
                <TableCell sx={{ width: 130 }}>
                  {dataEntryMode ? intl.formatMessage({ id: 'form.label.time' }) : intl.formatMessage({ id: 'abcHistory.dateTime' })}
                </TableCell>
                <TableCell sx={{ width: 200 }}>{intl.formatMessage({ id: 'dataCollection.field.antecedent' })}</TableCell>
                <TableCell sx={{ width: 200 }}>{intl.formatMessage({ id: 'dataCollection.field.behavior' })}</TableCell>
                <TableCell sx={{ width: 200 }}>{intl.formatMessage({ id: 'dataCollection.field.consequence' })}</TableCell>
                <TableCell sx={{ width: 80 }}>{intl.formatMessage({ id: 'dataCollection.field.duration' })}</TableCell>
                {dataEntryMode && <TableCell sx={{ width: 100 }}>{intl.formatMessage({ id: 'abcHistory.actions' })}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => {
                const actualIndex = page * rowsPerPage + index;
                const isToday = row.date && startOfDay(row.date).getTime() === startOfDay(new Date()).getTime();
                const canEdit = isToday && dataEntryMode;

                return (
                  <TableRow
                    key={row.id || index}
                    sx={{
                      cursor: !dataEntryMode ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: !dataEntryMode ? 'action.hover' : 'transparent'
                      }
                    }}
                    onClick={
                      !dataEntryMode
                        ? (e) => {
                            if (
                              e.target.tagName !== 'BUTTON' &&
                              e.target.tagName !== 'INPUT' &&
                              e.target.tagName !== 'svg' &&
                              e.target.tagName !== 'path'
                            ) {
                              handleViewRecord(row);
                            }
                          }
                        : undefined
                    }
                  >
                    <TableCell>
                      {canEdit ? (
                        <Select
                          value={row.setting || ''}
                          onChange={(e) => updateField(actualIndex, 'setting', e.target.value)}
                          size="small"
                          displayEmpty
                          fullWidth
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {commonSettings.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography variant="body2">{row.setting || '-'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {canEdit ? (
                        <TextField
                          type="time"
                          size="small"
                          multiline
                          rows={2}
                          value={row.time || ''}
                          onChange={(e) => updateField(actualIndex, 'time', e.target.value)}
                          sx={{ width: '100%' }}
                          InputLabelProps={{
                            shrink: true
                          }}
                        />
                      ) : (
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {row.date ? format(new Date(row.date), 'MMM dd, yyyy') : '-'}
                          </Typography>
                          <Typography variant="body2" fontSize="0.7rem" color="text.secondary">
                            {row.time || '-'}
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {canEdit ? (
                        <TextField
                          type="text"
                          size="small"
                          multiline
                          rows={2}
                          value={row.antecedent || ''}
                          onChange={(e) => updateField(actualIndex, 'antecedent', e.target.value)}
                          placeholder="What happened before?"
                          sx={{ width: '100%' }}
                        />
                      ) : (
                        <Typography variant="body2">{row.antecedent || '-'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {canEdit ? (
                        <TextField
                          type="text"
                          size="small"
                          multiline
                          rows={2}
                          value={row.behavior || ''}
                          onChange={(e) => updateField(actualIndex, 'behavior', e.target.value)}
                          placeholder="What did the child do?"
                          sx={{ width: '100%' }}
                        />
                      ) : (
                        <Typography variant="body2">{row.behavior || '-'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {canEdit ? (
                        <TextField
                          type="text"
                          size="small"
                          multiline
                          rows={2}
                          value={row.consequence || ''}
                          onChange={(e) => updateField(actualIndex, 'consequence', e.target.value)}
                          placeholder="What happened after?"
                          sx={{ width: '100%' }}
                        />
                      ) : (
                        <Typography variant="body2">{row.consequence || '-'}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {canEdit ? (
                        <TextField
                          type="text"
                          size="small"
                          value={row.duration ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d+$/.test(val)) {
                              updateField(actualIndex, 'duration', val === '' ? '' : parseInt(val));
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '') {
                              updateField(actualIndex, 'duration', 0);
                            }
                          }}
                          sx={{ width: '100%' }}
                        />
                      ) : (
                        <Typography variant="body2">{row.duration ? `${row.duration} min` : '-'}</Typography>
                      )}
                    </TableCell>
                    {dataEntryMode && (
                      <TableCell>
                        {canEdit && (
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={intl.formatMessage({ id: 'common.view', defaultMessage: 'View' })}>
                              <IconButton color="primary" size="small" onClick={() => handleViewRecord(row)}>
                                <EyeOutlined />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' })}>
                              <IconButton color="error" size="small" onClick={() => deleteRow(actualIndex)}>
                                <DeleteOutlined />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Stack sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredData.length)} of {filteredData.length} entries
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                size="small"
              >
                {[5, 10, 20, 50].map((pageSize) => (
                  <MenuItem key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </MenuItem>
                ))}
              </Select>
              <Pagination
                count={Math.ceil(filteredData.length / rowsPerPage)}
                page={page + 1}
                onChange={(event, value) => setPage(value - 1)}
                color="primary"
                shape="rounded"
              />
            </Stack>
          </Stack>
        </Stack>
      </MainCard>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={handleCloseNotesDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add/Edit Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={6}
            fullWidth
            placeholder={intl.formatMessage({ id: 'abcHistory.notesPlaceholder' })}
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotesDialog}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button onClick={handleSaveNotes} variant="contained">
            {intl.formatMessage({ id: 'common.save' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Record Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: 'abcHistory.recordDetails' })}</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              {/* Date and Time */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {intl.formatMessage({ id: 'form.label.date' })}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedRecord.date ? format(selectedRecord.date, 'MMMM dd, yyyy') : 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Time
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedRecord.time || 'N/A'}
                </Typography>
              </Grid>

              {/* Setting */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Setting
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedRecord.setting || 'N/A'}
                </Typography>
              </Grid>

              {/* Duration */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Duration
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedRecord.duration ? `${selectedRecord.duration} minutes` : 'N/A'}
                </Typography>
              </Grid>

              {/* Antecedent */}
              <Grid size={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1, fontWeight: 600 }}>
                  Antecedent (What happened before?)
                </Typography>
                <Typography variant="body1">{selectedRecord.antecedent || 'N/A'}</Typography>
              </Grid>

              {/* Behavior */}
              <Grid size={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1, fontWeight: 600 }}>
                  Behavior (What did the child do?)
                </Typography>
                <Typography variant="body1">{selectedRecord.behavior || 'N/A'}</Typography>
              </Grid>

              {/* Consequence */}
              <Grid size={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1, fontWeight: 600 }}>
                  Consequence (What happened after?)
                </Typography>
                <Typography variant="body1">{selectedRecord.consequence || 'N/A'}</Typography>
              </Grid>

              {/* Notes */}
              {selectedRecord.notes && (
                <Grid size={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedRecord.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

ABCHistoricalTable.propTypes = {
  data: PropTypes.array.isRequired,
  setData: PropTypes.func.isRequired,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  setStartDate: PropTypes.func.isRequired,
  setEndDate: PropTypes.func.isRequired,
  dataEntryMode: PropTypes.bool
};
